/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { useRouteMatch } from 'react-router-dom';
import { GroupBase, StylesConfig } from 'react-select';
import Select from 'react-select';
import { companyTrackerUserRoles, portfolioTrackerUserRoles, UserRoles } from '../../constants/user';
import { SelectOption } from '../../types/select';

type FilterValue = UserRoles | 'active' | 'pending' | undefined;

interface UserFilterProps {
  filterStatus?: FilterValue;
  handleStatusChange: (newValue: any) => void;
  filterPermission?: FilterValue;
  handlePermissionChange: (newValue: any) => void;
  defaultOptionText?: string;
}

const getStyles = (): StylesConfig<
  SelectOption<FilterValue>,
  boolean,
  GroupBase<SelectOption<FilterValue>>> => {
  const ravenGrey = '#6C757D';
  return {
    control: (provided) => ({
      ...provided,
      border: 'none',
    }),
    menu: (provided) => ({
      ...provided,
      marginTop: 0,
    }),
    input: (provided) => ({
      ...provided,
      color: ravenGrey,
      width: 120,
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      backgroundColor: 'transparent',
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: ravenGrey,
    }),
  }
}

export const UserFilter = ({
  filterStatus,
  handleStatusChange,
  filterPermission,
  handlePermissionChange,
  defaultOptionText = 'All user statuses',
}: UserFilterProps) => {
  const match = useRouteMatch<{ initiativeId?: string; portfolioId?: string }>();
  const isPortfolioTracker = !!match.params.portfolioId;
  const userRoles = isPortfolioTracker ? portfolioTrackerUserRoles : companyTrackerUserRoles;
  const options: { value: FilterValue; label: string }[] = [
    {
      value: undefined,
      label: defaultOptionText,
    },
    {
      value: 'active',
      label: 'Active Users',
    },
    {
      value: 'pending',
      label: 'Pending Users',
    },
  ];
  const currentValue = options.find((o) => o.value === filterStatus) || null;

  const permissionOptions: { value: FilterValue; label: string }[] = [
    {
      value: undefined,
      label: 'All user permissions',
    },
    ...userRoles.filter(r => r.code !== UserRoles.ContributorVerifier).map((r) => ({
      value: r.code,
      label: r.label,
    })),
  ];
  const currentPermissionValue = permissionOptions.find((o) => o.value === filterPermission) || null;

  return (
    <div className='d-flex'>
      <Select
        placeholder='Select user status'
        styles={getStyles()}
        options={options}
        value={currentValue}
        onChange={handleStatusChange}
      />
      <Select
        className='ml-2'
        placeholder='Select user permission'
        styles={getStyles()}
        options={permissionOptions}
        value={currentPermissionValue}
        onChange={handlePermissionChange}
      />
    </div>
  );
};
